import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight,
  Users,
  Settings,
  Heart,
  FileCheck,
  TrendingUp,
  Laptop,
  ClipboardCheck,
  GraduationCap,
  Shield,
  CheckCircle,
  ChevronDown,
} from "lucide-react";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import { useState } from "react";
import AnimateOnScroll from "../components/AnimateOnScroll";
import MidPageCTA from "../components/MidPageCTA";
import SectionDivider from "../components/SectionDivider";

const Services = () => {
  const services = [
    {
      icon: Users,
      title: "Staff Productivity & Workforce Management",
      description: "If your staff turnover is high, accountability is low, or you're constantly putting out HR fires. we help you build a team structure that works. Better hiring, clearer expectations, and a culture people want to stay in.",
      features: [
        "Hiring strategies that reduce turnover",
        "Performance systems & accountability tools",
        "Staff training and onboarding programs",
        "Team culture assessments & improvement plans",
      ],
    },
    {
      icon: Settings,
      title: "Day-to-Day Clinic Operations",
      description: "Things falling through the cracks? Scheduling headaches? Patient flow issues? We help you redesign the way your practice runs so you spend less time managing chaos and more time on what matters.",
      features: [
        "Workflow mapping & bottleneck removal",
        "Scheduling and resource optimization",
        "Front desk and patient intake improvements",
        "Standard operating procedures (SOPs) for your team",
      ],
    },
    {
      icon: Shield,
      title: "Compliance Readiness & Credentialing",
      description: "If the thought of a surprise audit makes your stomach drop, we should talk. We help you get compliant, stay organized, and stop guessing about what you might be missing.",
      features: [
        "Compliance gap assessments",
        "Credentialing and re-credentialing support",
        "Audit preparation and mock inspections",
        "Policy and procedure development",
      ],
    },
    {
      icon: Heart,
      title: "Patient Experience & Retention",
      description: "Great care isn't enough if patients don't feel it. We help you improve every touchpoint. from the first phone call to the follow-up. so patients come back and refer others.",
      features: [
        "Patient journey mapping",
        "Front desk and phone etiquette training",
        "Patient feedback and satisfaction systems",
        "Recall and retention strategies",
      ],
    },
    {
      icon: Laptop,
      title: "EHR Implementation & Practice Management Systems",
      description: "Choosing the wrong EHR can set you back months. Whether you're selecting a new system, switching platforms, or struggling to get your current one working. we guide you through the entire process.",
      features: [
        "EHR selection based on your practice's needs",
        "Implementation planning and project management",
        "Data migration and system configuration",
        "Staff training and adoption support",
      ],
    },
    {
      icon: TrendingUp,
      title: "Sustainable Growth & Scalable Systems",
      description: "Ready to grow but not sure your operations can handle it? We help you build the foundation. the systems, processes, and team structure. so growth doesn't create more problems than it solves.",
      features: [
        "Operational readiness assessments",
        "Telehealth integration and virtual care setup",
        "Multi-location expansion planning",
        "Revenue diversification strategies",
      ],
    },
  ];

  const process = [
    {
      step: "01",
      title: "Tell Us What's Going On",
      description: "We start by listening. your frustrations, your goals, what's keeping you up at night. No judgment, just understanding.",
    },
    {
      step: "02",
      title: "We Assess the Gaps",
      description: "Our team takes a close look at your operations, workflows, and team dynamics to find the real bottlenecks. not just the symptoms.",
    },
    {
      step: "03",
      title: "We Build a Clear Plan",
      description: "Together, we create a practical roadmap with realistic milestones. not a 50-page report that sits on a shelf collecting dust.",
    },
    {
      step: "04",
      title: "We Work Alongside You",
      description: "We don't hand off a plan and disappear. We're in the trenches with your team, implementing changes and solving problems as they come up.",
    },
    {
      step: "05",
      title: "We Make Sure It Sticks",
      description: "Before we wrap up, we make sure your team owns the new systems. You'll have the tools and confidence to keep the momentum going.",
    },
  ];

  return (
    <div>
      <SEO 
        title="Healthcare Consulting Services"
        description="Hands-on consulting for medical practices struggling with daily operations, staff productivity, EHR implementation, compliance readiness, and sustainable growth. We work alongside your team to fix what's not working."
        ogImage="https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/cku3b1xj_EnablingCollaborationFosteringTeamworkinHealthcareEnvironments-1719241343638.jpg"
        keywords="healthcare consulting services, clinic operations help, ehr implementation help, healthcare compliance consulting, medical practice workflow support, staff productivity healthcare, telehealth integration, practice management systems, healthcare audit preparation"
      />
      <StructuredData data={[
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.rnbconsultinggroup.com/services" }
          ]
        },
        {
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "RNB Consulting Group - Healthcare Consulting Services",
          "url": "https://www.rnbconsultinggroup.com/services",
          "description": "Hands-on consulting for medical practices struggling with daily operations, staff productivity, EHR implementation, compliance readiness, and sustainable growth.",
          "areaServed": [
            { "@type": "Country", "name": "United States" },
            { "@type": "Country", "name": "Canada" }
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Healthcare Consulting Services",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Staff Productivity & Workforce Management" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Day-to-Day Clinic Operations" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Compliance Readiness & Credentialing" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Patient Experience & Retention" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "EHR Implementation & Practice Management Systems" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Sustainable Growth & Scalable Systems" } }
            ]
          }
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map((faq) => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        }
      ]} />
      
      {/* Hero Section */}
      <section className="relative py-28 bg-gradient-to-br from-rnb-light to-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-blue/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-rnb-teal/5 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimateOnScroll direction="left">
              <div>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-rnb-navy mb-6">
                  Practical Help for the Real Challenges of Running a Practice
                </h1>
                <p className="text-lg text-rnb-gray mb-8">
                  You're juggling patient care, staff management, compliance deadlines, and a dozen other things. 
                  and none of it came with a manual. We provide hands-on support for clinic owners, private practices, 
                  home healthcare operators, and healthcare startups who need things to work better.
                </p>
                <Link to="/contact">
                  <Button size="lg" className="btn-hover-lift" data-testid="services-cta-btn">
                    Tell Us What You're Dealing With
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </AnimateOnScroll>
            <AnimateOnScroll direction="right">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/cku3b1xj_EnablingCollaborationFosteringTeamworkinHealthcareEnvironments-1719241343638.jpg"
                  alt="Healthcare professionals collaborating in a team discussion with clipboards"
                  className="img-warm w-full rounded-2xl shadow-xl h-56 sm:h-64 lg:h-auto object-cover"
                  data-testid="services-hero-image"
                />
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimateOnScroll>
            <div className="text-center mb-16">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
                What We Help With
              </h2>
              <p className="text-rnb-gray max-w-2xl mx-auto">
                Every practice has different pain points. Here's where we typically step in. 
                pick what sounds like your situation and we'll take it from there.
              </p>
            </div>
          </AnimateOnScroll>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <AnimateOnScroll key={service.title || service.name || service.label || index} delay={0.08 * index}>
                <Card
                  className="card-hover border-0 shadow-warm h-full"
                  data-testid={`service-${index}`}
                >
                  <CardContent className="p-9">
                    <div className="w-14 h-14 bg-rnb-light rounded-xl flex items-center justify-center mb-6">
                      <service.icon className="w-7 h-7 text-rnb-blue" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-3">
                      {service.title}
                    </h3>
                    <p className="text-rnb-gray text-sm mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center text-sm text-rnb-gray">
                          <CheckCircle className="w-4 h-4 text-rnb-teal mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Mid-page CTA */}
      <MidPageCTA variant={0} image="/images/healthcare/doctor-patient-checkup.jpg" />

      {/* Our Process */}
      <section className="py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimateOnScroll>
            <div className="text-center mb-16">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
                How We Work With You
              </h2>
              <p className="text-rnb-gray max-w-2xl mx-auto">
                We don't believe in cookie-cutter consulting. Here's what working with us actually looks like.
              </p>
            </div>
          </AnimateOnScroll>
          
          <div className="relative">
            {/* Connection line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-rnb-light -translate-y-1/2" />
            
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
              {process.map((step, index) => (
                <AnimateOnScroll key={step.title || step.name || i} delay={0.1 * index}>
                  <div className="relative">
                    <div className="bg-white rounded-xl p-6 shadow-sm text-center relative z-10">
                      <div className="w-12 h-12 bg-rnb-blue rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="font-mono-numbers text-lg font-bold text-white">
                          {step.step}
                        </span>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-2">
                        {step.title}
                      </h3>
                      <p className="text-rnb-gray text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </AnimateOnScroll>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimateOnScroll direction="left">
              <div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-6">
                  Who We Work With
                </h2>
                <p className="text-rnb-gray mb-8">
                  We've helped practices and organizations across the healthcare spectrum. from solo 
                  providers figuring out how to scale, to multi-location groups that need better systems.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {[
                    "Private Physician Practices",
                    "Home Healthcare Agencies",
                    "Hospice & Palliative Care",
                    "Biotech & Pharma Startups",
                    "Healthcare Startups",
                    "Urgent Care & Walk-in Clinics",
                    "Behavioral Health Providers",
                    "Specialty Clinics & Groups",
                  ].map((industry, index) => (
                    <div
                      key={industry.title || industry.name || `industry-${index}`}
                      className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg"
                    >
                      <CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" />
                      <span className="text-sm font-medium text-rnb-navy">{industry}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimateOnScroll>
            
            <AnimateOnScroll direction="right">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/ksnvz9vs_pexels-rdne-6129494.jpg"
                  alt="Diverse team of healthcare professionals in scrubs and white coats"
                  className="img-warm w-full"
                />
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <section className="py-28 bg-rnb-navy">
        <AnimateOnScroll>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
              Let's Build Something Better Together
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Whether you have a specific challenge in mind or just know things could run smoother. we'd love to hear from you. 
              No pressure, just an honest conversation about how we can help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-rnb-teal hover:bg-rnb-teal/90 btn-hover-lift" data-testid="services-bottom-cta">
                  Book a Free Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/programs/foundations-first">
                <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/10">
                  See Our Programs
                </Button>
              </Link>
            </div>
          </div>
        </AnimateOnScroll>
      </section>
    </div>
  );
};

export default Services;

const faqs = [
  {
    question: "How do I know if my clinic or practice actually needs consulting help?",
    answer: "If you're spending more time managing day-to-day operations than focusing on patient care and growth. that's a good sign you could benefit from outside support. Common indicators include high staff turnover, inconsistent workflows, compliance concerns you're not sure about, or growth that's creating more chaos than progress. We offer a free consultation to help you figure out if we're the right fit."
  },
  {
    question: "What size practices do you typically work with?",
    answer: "We specialize in small to mid-sized healthcare organizations. solo practitioners, group practices, home healthcare agencies, clinics with 5-50 staff, and healthcare startups. We bring the caliber of support that larger organizations have access to, but tailored for the realities and budgets of growing practices."
  },
  {
    question: "Can you help us choose and implement a new EHR or practice management system?",
    answer: "Absolutely. this is one of our core services. We help you evaluate systems based on your specific needs (not just what's popular), plan the implementation, manage data migration, train your staff, and make sure adoption actually sticks. Whether you're choosing your first system or switching from one that isn't working, we guide the entire process."
  },
  {
    question: "How long does a typical consulting engagement last?",
    answer: "It depends on the scope. Some projects. like a workflow assessment or compliance gap analysis. can be completed in 4-6 weeks. Larger engagements like EHR implementation, team restructuring, or multi-location expansion planning may run 3-6 months. We'll give you a clear timeline upfront so there are no surprises."
  },
  {
    question: "Do you work on-site or remotely?",
    answer: "Both. Some work is best done side-by-side with your team on-site, especially during initial assessments and hands-on training. But much of our operational support, planning, and ongoing consulting works well remotely. We'll recommend the best approach based on your situation."
  },
  {
    question: "How is your approach different from other healthcare consulting firms?",
    answer: "We don't do the big-firm thing where you get a 100-page report and a handshake. We work alongside your team. in the trenches. to implement real changes. Our focus is on practical, day-to-day improvements that your staff can actually sustain after we leave. And because we work exclusively with healthcare, we understand the specific challenges you face."
  },
  {
    question: "Can you help with compliance and getting ready for audits?",
    answer: "Yes. We help practices identify compliance gaps, build and update policies and procedures, prepare documentation, and run mock audits so you're ready before an inspector ever walks through the door. We also help with credentialing and re-credentialing workflows to keep everything current."
  },
  {
    question: "What does it cost to work with you?",
    answer: "Our pricing depends on the scope of the engagement. We offer project-based pricing, monthly retainer options, and our structured programs (like Foundations First) that have set pricing. The easiest way to get a clear picture is to book a free consultation or use our instant quote tool to get an estimate based on your needs."
  },
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  return (
    <section className="py-24 bg-white" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-rnb-gray max-w-2xl mx-auto">
            Answers to the questions we hear most from practice owners considering consulting support.
          </p>
        </div>
        
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={faq.question || faq.title || i}
              className="border border-slate-200 rounded-xl overflow-hidden transition-all duration-200 hover:border-rnb-teal/40"
              data-testid={`faq-item-${index}`}
            >
              <button
                className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-slate-50 transition-colors"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                data-testid={`faq-toggle-${index}`}
              >
                <span className="text-base font-semibold text-rnb-navy pr-4">{faq.question}</span>
                <ChevronDown 
                  className={`w-5 h-5 text-rnb-gray flex-shrink-0 transition-transform duration-200 ${openIndex === index ? 'rotate-180' : ''}`} 
                />
              </button>
              {openIndex === index && (
                <div className="px-5 pb-5 pt-0">
                  <p className="text-rnb-gray leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
